// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import HomePage from "@/views/public/HomePage.vue";
import DevelopersLandingPage from "@/views/public/DevelopersLandingPage.vue";
import JobDetails from "@/views/public/JobDetails.vue";
import JobsListing from "@/views/public/JobsListing.vue";
import LoginPage from "@/views/auth/LoginPage.vue";
import RegisterPage from "@/views/auth/RegisterPage.vue";
import ForgotPasswordPage from "@/views/auth/ForgotPasswordPage.vue";
import LoginSuccess from "@/views/auth/LoginSuccess.vue";
import OAuthCallback from "@/views/auth/OAuthCallback.vue";
// import OnboardingFlow from "@/views/onboarding/OnboardingFlow.vue"; // dead code, replaced by the wizard below
import JobseekerProfilePage from "@/views/profile/JobseekerProfilePage.vue";
import JobseekerSettingsPage from "@/views/settings/JobseekerSettingsPage.vue";
import SavedJobs from "@/views/protected/SavedJobs.vue";
import HiddenJobs from "@/views/protected/HiddenJobs.vue";
import ApplicationsPage from "@/views/protected/ApplicationsPage.vue";
import SubscriptionPlansPage from "@/views/subscription/SubscriptionPlansPage.vue";
import SubscriptionPage from "@/views/subscription/SubscriptionPage.vue";
import FeaturesPage from "@/views/public/Features.vue";
import SalaryGuidePage from "@/views/public/SalaryGuidePage.vue";
import BlogPage from "@/views/public/BlogPage.vue";
import ContactPage from "@/views/public/ContactPage.vue";
import PrivacyPolicyPage from "@/views/public/PrivacyPolicyPage.vue";
import TermsPage from "@/views/public/TermsPage.vue";
import RefundPolicyPage from "@/views/public/RefundPolicyPage.vue";
import InboxPage from "@/views/protected/InboxPage.vue";
import AdminDashboard from "@/views/admin/AdminDashboard.vue";
import authStore from "@/store/auth";
import { captureInviteCode } from "@/utils/inviteLink";
import {
  isAdminUser,
  isPublicRoute,
  isGuestPreviewActive,
  startGuestPreview,
  endGuestPreview,
} from "@/composables/useLandingVersion";

const routes = [
  // 5-step post-signup onboarding wizard
  {
    path: "/onboarding",
    name: "OnboardingWizard",
    component: () => import("@/views/onboarding/wizard/OnboardingWizard.vue"),
    meta: { requiresAuth: true, hideHeader: true },
  },
  // auth routes
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
    meta: { requiresGuest: true },
  },
  {
    path: "/register",
    name: "register",
    component: RegisterPage,
    meta: { requiresGuest: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPasswordPage,
    meta: { requiresGuest: true },
  },
  {
    path: "/login-success",
    name: "LoginSuccess",
    component: LoginSuccess,
    meta: { requiresGuest: true },
  },
  {
    path: "/oauth-callback",
    name: "OAuthCallback",
    component: OAuthCallback,
    meta: { requiresGuest: true },
  },
  {
    path: "/verify-email-code",
    name: "VerifyEmailCode",
    component: () => import("@/views/auth/VerifyEmailCodePage.vue"),
    meta: { requiresAuth: true, hideHeader: true },
  },
  // public routes
  {
    // HomeV3 is the default homepage. ownFooter: it renders its own footer (GuestNavbar is shared).
    path: "/",
    name: "Home",
    component: () => import("@/views/public/HomeV3.vue"),
    meta: { ownFooter: true },
  },
  {
    // Previous homepage, kept reachable for comparison.
    path: "/home-old",
    name: "HomeOld",
    component: HomePage,
  },
  {
    path: "/homev3",
    name: "HomeV3",
    component: () => import("@/views/public/HomeV3.vue"),
    meta: { ownFooter: true },
  },
  {
    // Where every link in the daily lead email lands (sent as an OnlineJobs.ph
    // job alert). ?job=<id> shows that job's teaser.
    path: "/onlinejobs",
    name: "OnlineJobsLanding",
    component: () => import("@/views/public/OnlineJobsLanding.vue"),
    meta: { ownFooter: true },
  },
  {
    // Where invite links shared in Telegram, Viber and WhatsApp groups land
    // (e.g. /olj?invite=tg-oct). OnlineJobs.ph only.
    path: "/olj",
    name: "OljLanding",
    component: () => import("@/views/public/OljLanding.vue"),
    meta: { ownFooter: true, navHome: "/olj" },
  },
  {
    path: "/hire-developers",
    name: "HireDevelopers",
    component: DevelopersLandingPage,
  },
  {
    path: "/features",
    name: "Features",
    component: FeaturesPage,
  },
  {
    path: "/salaries",
    name: "SalaryGuide",
    component: SalaryGuidePage,
  },
  {
    path: "/contact",
    name: "Contact",
    component: ContactPage,
  },
  {
    path: "/blog",
    name: "Blog",
    component: BlogPage,
  },
  {
    path: "/blog/:slug",
    name: "BlogDetail",
    component: () => import("@/views/public/BlogDetailPage.vue"),
  },
  {
    // Free public tools. appShell shows the app sidebar to logged-in users.
    path: "/tools/profile-optimizer",
    name: "ProfileOptimizer",
    component: () => import("@/views/public/tools/ProfileOptimizerPage.vue"),
    meta: { appShell: true },
  },
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: PrivacyPolicyPage,
  },
  {
    path: "/terms",
    name: "Terms",
    component: TermsPage,
  },
  {
    path: "/refund-policy",
    name: "RefundPolicy",
    component: RefundPolicyPage,
  },
  {
    path: "/jobs",
    name: "JobsList",
    component: JobsListing,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/jobs/:slug",
    name: "JobDetails",
    component: JobDetails,
    // No requiresAuth: this page stays viewable by guests (e.g. shared job
    // links). appShell just shows the app sidebar for users who ARE logged in.
    meta: { appShell: true },
  },
  // employer routes (admin only)
  {
    path: "/employers",
    name: "EmployersList",
    component: () => import("@/views/public/EmployersListPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/employers/:id",
    name: "EmployerProfile",
    component: () => import("@/views/public/EmployerProfilePage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/jobseekers/:id",
    name: "JobseekerPublicProfile",
    component: () => import("@/views/public/JobseekerPublicProfilePage.vue"),
  },
  {
    path: "/jobseekers/info/:id/",
    alias: "/jobseekers/info/:id",
    name: "JobseekerPublicProfileInfo",
    component: () => import("@/views/public/JobseekerPublicProfilePage.vue"),
  },
  {
    path: "/jobseekers/info/:name/:id/",
    alias: "/jobseekers/info/:name/:id",
    name: "JobseekerPublicProfileInfoWithName",
    component: () => import("@/views/public/JobseekerPublicProfilePage.vue"),
  },
  // jobseeker routes
  {
    path: "/profile",
    name: "Profile",
    component: JobseekerProfilePage,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/settings",
    name: "Settings",
    component: JobseekerSettingsPage,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/ai-instructions",
    name: "AIInstructions",
    component: () => import("@/views/settings/AISettingsPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/ai-settings",
    redirect: "/ai-instructions",
  },
  {
    path: "/saved-jobs",
    name: "SavedJobs",
    component: SavedJobs,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/hidden-jobs",
    name: "HiddenJobs",
    component: HiddenJobs,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/applications",
    name: "Applications",
    component: ApplicationsPage,
    meta: { requiresAuth: true, appShell: true },
  },
  // subscription plans are public; checkout still requires login (handled in the page)
  {
    path: "/subscription/plans",
    name: "SubscriptionPlans",
    component: SubscriptionPlansPage,
    meta: { allowExpiredSubscription: true, appShell: true },
  },
  {
    path: "/subscription/my",
    name: "MySubscription",
    component: SubscriptionPage,
    meta: { requiresAuth: true, allowExpiredSubscription: true, appShell: true },
  },
  {
    path: "/subscription/paymongo-checkout",
    redirect: "/subscription/plans",
  },
  {
    path: "/inbox",
    name: "Inbox",
    component: InboxPage,
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: () => import("@/views/protected/NotificationPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  // Admin routes
  {
    path: "/admin-dashboard",
    name: "AdminDashboard",
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/activity-logs",
    name: "ActivityLogs",
    component: () => import("@/views/admin/ActivityLogs.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/users",
    name: "AdminUsers",
    component: () => import("@/views/admin/UserList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/job-posts",
    name: "AdminJobPosts",
    component: () => import("@/views/admin/JobPosts.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/job-posts/daily",
    name: "AdminJobPostsDaily",
    component: () => import("@/views/admin/JobPostsDaily.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/applications",
    name: "AdminApplications",
    component: () => import("@/views/admin/Applications.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/email-events",
    name: "AdminEmailEvents",
    component: () => import("@/views/admin/EmailEvents.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/profile-optimizer",
    name: "AdminProfileOptimizer",
    component: () => import("@/views/admin/ProfileOptimizerRequests.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/support-tickets",
    name: "AdminSupportTickets",
    component: () => import("@/views/admin/SupportTickets.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/proxies",
    name: "AdminProxyList",
    component: () => import("@/views/admin/ProxyList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/scrapers",
    name: "AdminScraperList",
    component: () => import("@/views/admin/ScraperList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/auto-apply",
    name: "AdminAutoApplyMonitor",
    component: () => import("@/views/admin/AutoApplyMonitor.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/billing",
    name: "AdminBillingOverview",
    component: () => import("@/views/admin/BillingOverview.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/billing/users",
    name: "AdminBillingUsers",
    component: () => import("@/views/admin/BillingUsers.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/celery",
    name: "AdminCeleryMonitor",
    component: () => import("@/views/admin/CeleryMonitor.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/landing-page",
    name: "AdminLandingPage",
    component: () => import("@/views/admin/LandingPageSettings.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin-dashboard/jobhunter-dashboard",
    component: () => import("@/views/admin/recruitment/JobHunterDashboardLayout.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "",
        name: "JobHunterDashboard",
        component: () => import("@/views/admin/recruitment/JobHunterDashboard.vue"),
      },
      {
        path: "candidates",
        name: "AdminCandidates",
        component: () => import("@/views/admin/recruitment/JobHunterList.vue"),
      },
      {
        path: "candidates/:id",
        name: "AdminCandidateDetail",
        component: () => import("@/views/admin/recruitment/CandidateDetail.vue"),
      },
      {
        path: "outreach",
        name: "AdminOutreach",
        component: () => import("@/views/admin/recruitment/OutreachTracker.vue"),
      },
      {
        path: "lead-pipeline",
        name: "AdminLeadPipeline",
        component: () => import("@/views/admin/recruitment/LeadPipeline.vue"),
      },
      {
        path: "campaigns",
        name: "AdminCampaigns",
        component: () => import("@/views/admin/recruitment/CampaignList.vue"),
      },
      {
        path: "campaigns/new",
        name: "AdminCampaignNew",
        component: () => import("@/views/admin/recruitment/CampaignBuilder.vue"),
      },
      {
        path: "campaigns/:id/edit",
        name: "AdminCampaignEdit",
        component: () => import("@/views/admin/recruitment/CampaignBuilder.vue"),
      },
      {
        path: "campaigns/:id",
        name: "AdminCampaignResults",
        component: () => import("@/views/admin/recruitment/CampaignResults.vue"),
      },
      {
        path: "clients",
        name: "AdminClients",
        component: () => import("@/views/admin/recruitment/EmployerPipeline.vue"),
      },
      {
        path: "clients/:id",
        name: "AdminClientDetail",
        component: () => import("@/views/admin/recruitment/ClientDetail.vue"),
      },
      {
        path: "placements",
        name: "AdminPlacements",
        component: () => import("@/views/admin/recruitment/PlacementsList.vue"),
      },
    ],
  },
  // Courses routes
  {
    path: "/courses",
    name: "Courses",
    component: () => import("@/views/public/CoursesPage.vue"),
  },
  {
    path: "/courses/:slug",
    name: "CourseDetail",
    component: () => import("@/views/public/CourseDetailPage.vue"),
  },
  {
    path: "/courses/:slug/lessons/:lessonId",
    name: "CourseLesson",
    component: () => import("@/views/protected/CourseLessonPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  // Jobseeker dashboard & tooling
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/protected/DashboardPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/getting-started",
    name: "GettingStarted",
    component: () => import("@/views/protected/GettingStartedPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/job-filters",
    name: "JobFilters",
    component: () => import("@/views/protected/JobFiltersPage.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/notification-settings",
    redirect: "/notifications",
  },
  {
    path: "/auto-apply",
    name: "AutoApply",
    component: () => import("@/views/protected/AutoApplyPage.vue"),
    meta: { requiresAuth: true, appShell: true, requiresOlj: true },
  },
  {
    path: "/contact-us",
    name: "ContactUs",
    component: () => import("@/views/ContactUsView.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  {
    path: "/extension/connect",
    name: "ExtensionConnect",
    component: () => import("@/views/ExtensionConnect.vue"),
    meta: { requiresAuth: true, appShell: true },
  },
  // Docs routes
  {
    path: "/docs",
    component: () => import("@/views/docs/DocsLayout.vue"),
    children: [
      { path: "", redirect: "/docs/getting-started" },
      {
        path: "getting-started",
        name: "DocsGetStarted",
        component: () => import("@/views/docs/DocsGetStarted.vue"),
      },
      {
        path: "job-filters",
        name: "DocsJobFilters",
        component: () => import("@/views/docs/DocsJobFilters.vue"),
      },
      {
        path: "notifications",
        name: "DocsNotifications",
        component: () => import("@/views/docs/DocsNotifications.vue"),
      },
      {
        path: "notifications/email",
        name: "DocsEmailNotifications",
        component: () => import("@/views/docs/DocsEmailNotifications.vue"),
      },
      {
        path: "notifications/telegram",
        name: "DocsTelegramNotifications",
        component: () => import("@/views/docs/DocsTelegramNotifications.vue"),
      },
      {
        path: "notifications/discord",
        name: "DocsDiscordNotifications",
        component: () => import("@/views/docs/DocsDiscordNotifications.vue"),
      },
    ],
  },
  // 404 catch-all route
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/public/NotFound.vue"),
    // appShell keeps the sidebar visible for logged-in users who hit a dead
    // link, so they don't lose their navigation on a 404.
    meta: { appShell: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  // Remember a campaign ?invite= code from whichever page a visitor lands on.
  if (to.query.invite) captureInviteCode(to.query);
  const hasToken = !!localStorage.getItem("token");
  if (hasToken && !authStore.state.isLoggedIn) {
    try {
      await authStore.checkAuthStatus();
    } catch (e) {
      // ignore
    }
  }

  // Admin guest preview (Admin → Landing Page): /?preview=1 starts it, and it ends as
  // soon as the admin opens a login-only or guest-only page (e.g. Log In → Dashboard).
  if (isAdminUser(authStore.state.user)) {
    if (to.name === "Home" && to.query.preview) {
      startGuestPreview();
    } else if (!isPublicRoute(to)) {
      endGuestPreview();
    }
  }

  // Redirect logged-in users away from the home page to the dashboard,
  // unless an admin is previewing the site as a visitor.
  if (
    to.name === "Home" &&
    authStore.state.isLoggedIn &&
    !isGuestPreviewActive(authStore.state.user)
  ) {
    next({ name: "Dashboard" });
    return;
  }

  // Check if route requires authentication
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const isLoggedIn = authStore.state.isLoggedIn;

    if (!isLoggedIn) {
      // Redirect to login if not logged in, preserving the intended destination
      next({ name: "Login", query: { redirect: to.fullPath } });
      return;
    }

    // Block access to protected routes until the user has verified their email.
    // Fully-public routes (no requiresAuth) are left alone since they don't require login anyway.
    if (
      to.name !== "VerifyEmailCode" &&
      authStore.state.user?.email_verified === false
    ) {
      next({ name: "VerifyEmailCode" });
      return;
    }

    // Send jobseekers who never finished the onboarding wizard back to it —
    // this runs on every navigation (fresh tab, bookmark, reload), not just
    // right after login/registration/OAuth.
    if (
      to.name !== "OnboardingWizard" &&
      to.name !== "VerifyEmailCode" &&
      authStore.state.user?.account_type === "jobseeker" &&
      authStore.state.user?.onboarding_completed === false
    ) {
      next({ name: "OnboardingWizard" });
      return;
    }

    // Check if route requires admin privileges
    if (to.matched.some((record) => record.meta.requiresAdmin)) {
      const user = authStore.state.user;
      const isAdmin =
        user &&
        (user.is_superuser ||
          user.account_type === "admin" ||
          user.admin_profile);

      if (!isAdmin) {
        // Redirect to home if not admin
        next({ name: "Home" });
        return;
      }
    }

    // Auto Apply is admin-gated via olj_jobs_enabled — everyone else bounces to dashboard
    if (to.matched.some((record) => record.meta.requiresOlj)) {
      if (!authStore.state.user?.olj_jobs_enabled) {
        next({ name: "Dashboard" });
        return;
      }
    }
  }

  // Prevent logged-in users from visiting guest-only routes (same landing spot as Home)
  if (to.matched.some((record) => record.meta.requiresGuest)) {
    const isLoggedIn = authStore.state.isLoggedIn;
    if (isLoggedIn) {
      next({ name: "Dashboard" });
      return;
    }
  }

  // Continue to the route
  next();
});

export default router;
